import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Play, Radio, Music, Mic, Bookmark, CalendarDays, ShoppingBag } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "podcast", label: "Podcast", Icon: Mic, kinds: ["podcast", "audio_teaching", "video_teaching", "reflection", "article"] },
  { id: "radio", label: "Radio", Icon: Radio },
  { id: "music", label: "Music", Icon: Music, kinds: ["music"] },
];

const fmtTime = (t) => { const [h, m] = (t || "").split(":").map(Number); if (isNaN(h)) return t || ""; return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`; };

const EmptyState = ({ text }) => (
  <div className="rounded-2xl border p-10 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
    <div className="eyebrow mb-2">Coming soon</div>
    <p className="font-serif text-lg" style={{ color: "var(--burgundy)" }}>{text}</p>
  </div>
);

export default function Listen() {
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some((t) => t.id === sp.get("tab")) ? sp.get("tab") : "podcast";
  const setTab = (id) => setSp(id === "podcast" ? {} : { tab: id }, { replace: true });
  const highlight = sp.get("episode");
  const [teachings, setTeachings] = useState([]);
  const [podcastEpisodes, setPodcastEpisodes] = useState([]);
  const [music, setMusic] = useState([]);
  const [radioCfg, setRadioCfg] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playTrack, toggleSave, isSaved } = useApp();

  useEffect(() => {
    if (!highlight || loading) return;
    const el = document.querySelector(`[data-testid="podcast-card-${highlight}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight, loading]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const [t, m, r, p, sch] = await Promise.all([
          api.get("/content/teachings"),
          api.get("/store/music").catch(() => ({ data: [] })),
          api.get("/content/radio-config"),
          api.get("/content/podcast/episodes").catch(() => ({ data: [] })),
          api.get("/content/radio-schedule").catch(() => ({ data: [] })),
        ]);
        if (ignore) return;
        setTeachings((t.data || []).filter((x) => x.kind !== "music" && x.kind !== "podcast"));
        setMusic(m.data || []);
        setRadioCfg(r.data);
        setPodcastEpisodes(p.data || []);
        setSchedule(sch.data || []);
      } catch {}
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, []);

  const preparePlay = (t) => ({ ...t, art: t.art_url || t.art });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <div className="mb-10 rise">
        <div className="eyebrow mb-2">Listen</div>
        <h1 className="font-serif text-4xl sm:text-5xl" style={{ color: "var(--burgundy)" }}>Sound as sanctuary.</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--text-muted)" }}>
          Teachings for the interior life. Tune in live, dwell with an episode, or let contemplative music hold you a while.
        </p>
      </div>

      <div className="inline-flex p-1 rounded-full border mb-8" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }}>
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} data-testid={`listen-tab-${id}-btn`} onClick={() => setTab(id)}
            className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full text-sm font-medium"
            style={{ background: tab === id ? "var(--burgundy)" : "transparent", color: tab === id ? "var(--bg-cream)" : "var(--text-obsidian)" }}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "podcast" && (() => {
        if (loading) return null;
        const items = [
          ...podcastEpisodes.map((e) => ({
            id: e.guid,
            title: e.title,
            description: e.description,
            host: e.host,
            duration: e.duration,
            released_at: e.published_at ? new Date(e.published_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null,
            episode: e.episode ? `Episode ${e.episode}` : null,
            season: e.season,
            audio_url: e.audio_url,
            art_url: e.art_url,
            kind: "podcast",
          })),
          ...teachings,
        ];
        if (items.length === 0) return <EmptyState text="New teachings are being prepared." />;
        return (
          <div className="grid gap-6 md:grid-cols-2">
            {items.map((p) => (
              <article key={p.id} className="rounded-2xl border p-5 lift flex gap-5 min-w-0 overflow-hidden" style={{ background: "var(--bg-cream-card)", borderColor: highlight === p.id ? "var(--gold)" : "var(--border-cream)", borderWidth: highlight === p.id ? 2 : 1 }} data-testid={`podcast-card-${p.id}`}>
                {(p.art_url || p.art) && <img src={p.art_url || p.art} alt={p.title} className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl object-cover flex-shrink-0" />}
                <div className="min-w-0 flex flex-col">
                  <div className="eyebrow mb-1 whitespace-normal break-words">{[p.episode, p.duration, p.released_at].filter(Boolean).join(" · ")}</div>
                  <h3 className="font-serif text-xl sm:text-2xl leading-tight" style={{ color: "var(--burgundy)" }}>{p.title}</h3>
                  {p.host && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>with {p.host}</div>}
                  {(p.description || p.summary) && <p className="text-sm mt-3 leading-relaxed line-clamp-3" style={{ color: "var(--text-obsidian)" }}>{p.description || p.summary}</p>}
                  <div className="mt-auto pt-4 flex items-center gap-2">
                    <button data-testid={`podcast-play-${p.id}-btn`} onClick={() => { playTrack(preparePlay(p)); toast(`Playing "${p.title}"`); }}
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium"
                      style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>
                      <Play className="w-3.5 h-3.5" /> Play
                    </button>
                    <button data-testid={`podcast-save-${p.id}-btn`} onClick={() => { toggleSave(p, "teaching"); toast("Saved to My Temple"); }}
                      className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs border"
                      style={{ borderColor: "var(--border-cream)", color: "var(--burgundy)" }}>
                      <Bookmark className={`w-3.5 h-3.5 ${isSaved(p.id) ? "fill-current" : ""}`} /> Save
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        );
      })()}

      {tab === "radio" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-3xl p-8 relative overflow-hidden" style={{ background: "var(--obsidian)", color: "var(--bg-cream)" }}>
            <div className="grain absolute inset-0" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full pulse-gold" style={{ background: "var(--gold)" }} />
                <span className="eyebrow" style={{ color: "var(--gold-soft)" }}>{radioCfg?.listen_live_enabled ? "Streaming Live" : "Live stream not yet connected"}</span>
              </div>
              <div className="font-serif text-3xl sm:text-4xl mb-2" style={{ color: "var(--gold)" }}>{radioCfg?.station_name || "Temple Radio"}</div>
              <div className="text-sm opacity-90 mb-8">{radioCfg?.description || "Temple Radio streams on Citrus3. The Listen Live player will connect here."}</div>
              <div className="flex items-center gap-5">
                <button data-testid="listen-live-radio-btn"
                  disabled={!radioCfg?.listen_live_enabled || !radioCfg?.stream_url}
                  onClick={() => {
                    if (!radioCfg?.stream_url) { toast("Live stream not yet connected"); return; }
                    playTrack({
                      id: "temple-radio-live",
                      kind: "radio",
                      title: radioCfg.station_name || "Temple Radio",
                      host: radioCfg.description || "Live",
                      duration: "LIVE",
                      art: radioCfg.art_url,
                      audio_url: radioCfg.stream_url,
                    });
                    toast("Live on Temple Radio");
                  }}
                  className="w-16 h-16 rounded-full flex items-center justify-center disabled:opacity-50"
                  style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                  <Play className="w-6 h-6" />
                </button>
                <div className="waveform text-[color:var(--gold)] text-2xl"><span/><span/><span/><span/><span/><span/><span/></div>
              </div>
              <div className="hairline my-8" />
              <div className="text-sm opacity-85">
                "Radio here is a hearth, not a broadcast. Sit with us for as long as you need."
              </div>
            </div>
          </div>
          <div className="rounded-2xl border p-6" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid="radio-schedule">
            <div className="eyebrow mb-4 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Schedule</div>
            {schedule.length > 0 ? (
              <ul className="space-y-4">
                {schedule.map((b) => (
                  <li key={b.id} data-testid={`radio-schedule-block-${b.id}`}>
                    <div className="font-serif text-lg leading-tight" style={{ color: "var(--burgundy)" }}>{b.title}</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                        <span key={d} className="rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest" style={{ background: (b.days || []).includes(d) ? "var(--burgundy)" : "transparent", color: (b.days || []).includes(d) ? "var(--bg-cream)" : "var(--text-muted)", border: "1px solid var(--border-cream)" }}>{d}</span>
                      ))}
                    </div>
                    <div className="text-sm mt-2" style={{ color: "var(--text-obsidian)" }}>{fmtTime(b.start_time)} – {fmtTime(b.end_time)}{b.time_zone ? ` ${b.time_zone}` : ""}</div>
                    {b.description && <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--text-muted)" }}>{b.description}</p>}
                  </li>
                ))}
              </ul>
            ) : radioCfg?.schedule_text ? (
              <div className="text-sm whitespace-pre-line" style={{ color: "var(--text-obsidian)" }}>{radioCfg.schedule_text}</div>
            ) : (
              <div className="text-sm" style={{ color: "var(--text-muted)" }}>Temple Radio streams around the clock. Live broadcast times will be posted here.</div>
            )}
          </div>
        </div>
      )}

      {tab === "music" && (
        loading ? null : music.length === 0 ? <EmptyState text="Contemplative music offerings are coming soon." /> : (
          <div data-testid="listen-music-grid">
            <p className="text-sm mb-6 max-w-2xl" style={{ color: "var(--text-muted)" }}>Albums from the Temple. Listen to a preview here; complete recordings are available in the Store.</p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {music.map((m) => (
                <div key={m.id} className="rounded-2xl overflow-hidden border lift flex flex-col" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`music-card-${m.id}`}>
                  <div className="relative h-56" style={{ background: "var(--surface-deep)" }}>
                    {m.image_url ? <img src={assetUrl(m.image_url)} alt={m.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><Music className="w-10 h-10" style={{ color: "var(--gold)" }} /></div>}
                    {m.has_preview && (
                      <button data-testid={`music-play-${m.id}-btn`} onClick={() => { playTrack({ id: `music-${m.id}`, kind: "music", title: m.title, host: m.subtitle || "Preview", duration: "PREVIEW", art: m.image_url ? assetUrl(m.image_url) : null, audio_url: assetUrl(m.preview_url) }); toast(`Playing preview of "${m.title}"`); }}
                        className="absolute bottom-3 right-3 w-11 h-11 rounded-full flex items-center justify-center"
                        style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="font-serif text-lg leading-tight" style={{ color: "var(--burgundy)" }}>{m.title}</div>
                    {m.subtitle && <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{m.subtitle}</div>}
                    {m.description && <p className="text-sm mt-3 leading-relaxed line-clamp-3" style={{ color: "var(--text-obsidian)" }}>{m.description}</p>}
                    <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                      <span className="text-xs font-mono" style={{ color: "var(--gold-dark)" }} data-testid={`music-price-${m.id}`}>{m.price_cents ? `$${(m.price_cents / 100).toFixed(2)}` : ""}{!m.has_preview ? (m.price_cents ? " · " : "") + "No preview yet" : ""}</span>
                      <Link to="/store" data-testid={`music-store-${m.id}-link`} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }}>
                        <ShoppingBag className="w-3.5 h-3.5" /> In the Store
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}
